import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";

import { useProject } from "../app/projectContext";
import {
  createHarmonicAssessmentRun,
  type HarmonicAssessmentRunCreateRequest,
  type HarmonicAssessmentRunResponse,
} from "../services/harmonics";

/**
 * Harmonic assessment study mutation (EOS-11b, A32): the rules of usePfCorrection - one
 * in-flight request at a time, aborted on unmount, the run attached to the revision chosen in
 * the top bar at submit time, the typed result and the run summary exposed as parsed.
 */
export function useHarmonics() {
  const controllerRef = useRef<AbortController | null>(null);
  const { activeRevisionId } = useProject();

  const mutation = useMutation<
    HarmonicAssessmentRunResponse,
    Error,
    HarmonicAssessmentRunCreateRequest
  >({
    mutationKey: ["electrical", "harmonics", "runs", "create"],
    mutationFn: (payload) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      return createHarmonicAssessmentRun(
        payload,
        controller.signal,
        activeRevisionId() ?? undefined,
      );
    },
  });

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const calculate = useCallback(
    (payload: HarmonicAssessmentRunCreateRequest) => mutation.mutateAsync(payload),
    [mutation],
  );

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    mutation.reset();
  }, [mutation]);

  return {
    calculate,
    reset,
    result: mutation.data?.result ?? null,
    run: mutation.data?.run ?? null,
    error: mutation.error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
}

export type UseHarmonicsResult = ReturnType<typeof useHarmonics>;
